import React from "react";
import { Field, ErrorMessage, useFormikContext } from "formik";
import { Form } from "react-bootstrap";
import axios from "axios";
import { MultiSelect } from "react-multi-select-component";
import PdfFileIcon from "../../assests/img/pdf-file-icon.svg";
import CSVIcon from "../../assests/img/csv.svg";
import XLSXIcon from "../../assests/img/xlsx.svg";
import PowerPointIcon from "../../assests/img/powerpoint.svg";
import VideoIcon from "../../assests/img/video.svg";
import DocIcon from "../../assests/img/doc.svg";
import ZipIcon from "../../assests/img/zip.svg";
import AttachementRemoveIcon from "../../assests/img/Attachement-close-icon.svg";

const DynamicField = ({ field, setFieldValue, formConfig }) => {
  const { label, key, type, placeholder, required, options, fields, multiple, visibilityConditions } = field.fieldId || field;
  const isEditable = field.fieldId ? field.fieldId.isEditable : field.isEditable;
  const { values } = useFormikContext(); // Access Formik context to get form values

  // Helper function to get the value of a nested field
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((current, key) => {
      if (key.includes('[')) {
        const [base, index] = key.split(/\[|\]/).filter(Boolean);
        return current?.[base]?.[parseInt(index)];
      }
      return current?.[key];
    }, obj);
  };

  const isFieldVisible = () => {
    if (key.includes('.subReason')) {
      const index = key.match(/\.(\d+)\./)?.[1];
      if (!index) {
        console.debug(`No index found for subReason key: ${key}`);
        return false;
      }
      const reasonFieldKey = `formInfo.meterData.${index}.reason`;
      const reasonValue = getNestedValue(values, reasonFieldKey);
      console.debug(`subReason visibility: key=${key}, reasonFieldKey=${reasonFieldKey}, reasonValue=${reasonValue}`);
      return reasonValue === 'Others';
    }

    if (!visibilityConditions) return true;

    const index = key.match(/\.(\d+)\./)?.[1];
    if (!index) {
      console.debug(`No index found for key: ${key}`);
      return false;
    }

    const reasonFieldKey = `formInfo.meterData.${index}.reason`;
    const reasonValue = getNestedValue(values, reasonFieldKey);
    console.debug(`subReason visibility: key=${key}, reasonFieldKey=${reasonFieldKey}, reasonValue=${reasonValue}`);
    return reasonValue === 'Client Usage';
  };

  if (!isFieldVisible()) return null;

  if (fields && fields.length > 0) {
    return (
      <div className="GroupedForm">
        <label className="GroupedForm_Label mb-3">{label}</label>
        <div className="row">
          {fields.map((nestedField, index) => {
            if (nestedField.type === "separator") {
              return (
                <div key={`separator-${index}`} className="ObjSeparater">
                </div>
              );
            }
            return (
              <div key={nestedField.key || index} className="col-md-6">
                <div className="GroupedForm_Fields">
                  <DynamicField
                    field={nestedField}
                    setFieldValue={setFieldValue}
                    formConfig={formConfig}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const uploadDocument = async (file) => {
    if (!file) return null;

    try {
      const attachments = {
        bucket: `${process.env.REACT_APP_BUCKET_NAME}`,
        key: `attachment/${file.name}`,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_FILE_UPLOAD}fileupload`,
        attachments
      );

      const uploadUrl = response.data.uploadUrl;
      const fileKey = response.data.key;

      if (!uploadUrl) {
        throw new Error("Failed to get upload URL.");
      }

      const binaryData = await file.arrayBuffer();
      const uploadResponse = await axios.put(uploadUrl, binaryData, {
        headers: {
          "Content-Type": file.type || "binary/octet-stream",
          "Content-Length": binaryData.length,
        },
      });

      if (uploadResponse.status === 200) {
        return `${process.env.REACT_APP_AWS_BUCKET_PATH}${fileKey}`;
      } else {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Upload failed. Please try again.");
      return null;
    }
  };

  const handleImageChange = async (event, setFieldValue, key) => {
    const file = event.target.files[0];
    if (!file) return;

    const fileUrl = await uploadDocument(file);
    if (fileUrl) {
      const currentValue = event.target.form.elements[key].value;
      if (currentValue) {
        const existingFiles = Array.isArray(currentValue) ? currentValue : [currentValue];
        setFieldValue(key, [...existingFiles, fileUrl]);
      } else {
        setFieldValue(key, [fileUrl]);
      }
    }
  };

  const handleRemoveFile = (key, index) => {
    setFieldValue((prev) => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
  };

    // Determine if the field is required (specifically for subReason when reason is "Others")
  const isFieldRequired = () => {
    if (key.includes('.subReason')) {
      const index = key.match(/\.(\d+)\./)?.[1];
      if (!index) return required;
      const reasonFieldKey = `formInfo.meterData.${index}.reason`;
      const reasonValue = getNestedValue(values, reasonFieldKey);
      return reasonValue === 'Others' ? true : required;
    }
    return required;
  };


  const ValueRendererClients = (selectedClients, optionsClients) => {
    if (selectedClients.length === optionsClients.length) {
      console.log(selectedClients.length === optionsClients.length,"dssss")
      return "All clients";
    } else if (selectedClients.length === 1) {
      return optionsClients.find(opt => opt.value === selectedClients[0])?.label || "1 client selected";
    } else {
      return selectedClients.length > 0
        ? `${selectedClients.length} clients selected`
        : "Select Clients";
    }
  };


  switch (type) {
    case "text":
    case "number":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <Field
            id={key}
            className="formInput form-control"
            name={key}
            type={type}
            placeholder={placeholder}
            required={required}
            disabled={!isEditable}
          />
        </Form.Group>
      );

      case "select":
        if (multiple) {
          return (
            <Form.Group key={key} className="FormGroup">
              <label className="formLabel form-label" htmlFor={key}>
                {label} {required && <span style={{ color: "red" }}>*</span>}
              </label>
              <Field name={key}>
                {({ field, form }) => (
                  <MultiSelect
                    options={options}
                    value={field.value ? options.filter(opt => field.value.includes(opt.value)) : []}
                    onChange={(selected) => {
                      const selectedValues = selected.map(opt => opt.value);
                      form.setFieldValue(key, selectedValues);
                    }}
                    labelledBy={label}
                    disabled={!isEditable}
                    placeholder={`Select ${label}`}
                    hasSelectAll={true}
                    hasReset={true}
                    isSearchable={true}
                    valueRenderer={(selected) => ValueRendererClients(selected, options)} // Use valueRenderer
                  />
                )}
              </Field>
              <ErrorMessage name={key} component="div" className="text-danger" />
            </Form.Group>
          );
        }
        // Rest of the select case for single select remains unchanged
        return (
          <Form.Group key={key} className="FormGroup">
            <label className="formLabel form-label" htmlFor={key}>
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <Field
              as="select"
              id={key}
              name={key}
              required={required}
              className="FormSelect form-select"
              disabled={!isEditable}
              multiple={false}
            >
              <option value="">Select {label}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Field>
          </Form.Group>
        );

    case "file":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <Field name={key}>
            {({ field: formikField, form }) => {
              const isEditable = field?.isEditable;
              return (
                <>
                  {isEditable && (
                    <input
                      id={key}
                      type="file"
                      className="formInput form-control"
                      required={required}
                      onChange={async (event) => {
                        const file = event.target.files[0];
                        if (!file) return;

                        const fileUrl = await uploadDocument(file);
                        if (fileUrl) {
                          const currentValue = formikField.value;
                          if (currentValue) {
                            const existingFiles = Array.isArray(currentValue) ? currentValue : [currentValue];
                            form.setFieldValue(key, [...existingFiles, fileUrl]);
                          } else {
                            form.setFieldValue(key, [fileUrl]);
                          }
                        }
                      }}
                      disabled={!isEditable}
                    />
                  )}
                  <ErrorMessage name={key} component="div" className="text-danger" />
                  <div className="mt-2">
                    <div className="d-flex flex-wrap">
                      {formikField.value && Array.isArray(formikField.value) && formikField.value.map((url, index) => {
                        if (!url) return null;
                        const fileExtension = url.split('.').pop().toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);
                        const isPdf = fileExtension === 'pdf';
                        const isDoc = ['doc', 'docx'].includes(fileExtension);
                        const isPowerPoint = ['ppt', 'pptx'].includes(fileExtension);
                        const isExcel = ['xls', 'xlsx'].includes(fileExtension);
                        const isZip = ['zip', 'rar', '7z'].includes(fileExtension);
                        const isVideo = ['mp4', 'avi', 'mov', 'wmv'].includes(fileExtension);
                        return (
                          <div key={index} className="AttachedFileBox" style={{ margin: "5px" }}>
                            <div className="AttachedFileView">
                              {isPdf ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={PdfFileIcon}
                                    alt="PDF Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isDoc ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={DocIcon}
                                    alt="Doc Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isExcel ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={XLSXIcon}
                                    alt="Excel Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isZip ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={ZipIcon}
                                    alt="Zip Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isVideo ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={VideoIcon}
                                    alt="Video Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isPowerPoint ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={PowerPointIcon}
                                    alt="PowerPoint Attachment"
                                    style={{ objectFit: "cover" }}
                                  />
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              ) : isImage ? (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <img
                                    src={url}
                                    alt=""
                                    style={{
                                      width: "60px",
                                      height: "60px",
                                      objectFit: "cover",
                                    }}
                                  />
                                </div>
                              ) : (
                                <div onClick={() => window.open(url, '_blank')}>
                                  <i className="fas fa-file fa-2x text-secondary"></i>
                                  <div className="FileName1">
                                    {url.split('/').pop()}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            }}
          </Field>
        </Form.Group>
      );

    case "long-text":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {isFieldRequired() && <span style={{ color: "red" }}>*</span>}
          </label>
          <Field
            id={key}
            className="formTextarea form-control"
            name={key}
            as="textarea"
            required={isFieldRequired()}
            placeholder={placeholder}
            disabled={!isEditable}
          />
          <ErrorMessage name={key} component="div" className="text-danger" />
        </Form.Group>
      );    

    case "radio":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="form-check-group">
            {options.map((option) => (
              <div key={option.value} className="form-check">
                <Field
                  type="radio"
                  name={key}
                  id={`${key}-${option.value}`}
                  value={option.value}
                  required={required}
                  className="form-check-input"
                  disabled={!isEditable}
                />
                <label
                  htmlFor={`${key}-${option.value}`}
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </Form.Group>
      );

    case "checkbox":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <div className="form-check-group">
            {options.map((option) => (
              <div key={option.value} className="form-check">
                <Field
                  type="checkbox"
                  name={key}
                  id={`${key}-${option.value}`}
                  value={option.value}
                  required={required}
                  className="form-check-input"
                  disabled={!isEditable}
                />
                <label
                  htmlFor={`${key}-${option.value}`}
                  className="form-check-label"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </Form.Group>
      );

    case "date":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <Field name={key}>
            {({ field: formikField, form }) => {
              const formatDate = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                // const hours = String(date.getHours()).padStart(2, '0');
                // const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${day}/${month}/${year}`;
              };
              return (
                <input
                  id={key}
                  type="text"
                  className="formInput form-control"
                  value={formatDate(formikField.value)}
                  required={required}
                  disabled={!isEditable}
                />
              );
            }}
          </Field>
        </Form.Group>
      );

    case "time":
      return (
        <Form.Group key={key} className="FormGroup">
          <label className="formLabel form-label" htmlFor={key}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </label>
          <Field
            id={key}
            name={key}
            type="time"
            className="formInput form-control"
            required={required}
            disabled={!isEditable}
          />
        </Form.Group>
      );

    default:
      return null;
  }
};

export default DynamicField;